import React, { useState } from 'react';
import styled from '@emotion/styled';
import { BsSearch } from 'react-icons/bs';
import { FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import {
  ConversationCheckAvailability,
  ConversationCheckAvailabilityProducts,
  CheckAvailabilityRecommendations,
  CheckAvailabilityProduct,
} from '@tomis-tech/types';

import {
  Overlay,
  ErrorMessage,
  Button,
  ButtonsContainer,
  ScrollContainer,
  TitleText,
} from './styled-components';
import { SavingSpinner } from './common';
import { getTourDisplayName } from '../utils';
import { useCheckAvailability, useSession, useParent } from '../context';
import {
  themeBlue,
  SEARCH,
  DEFAULT_CHECK_AVAILABILITY_PRODUCTS,
} from '../consts';
import { createCheckAvailabilityUserActionMessage } from '../messages';
import { updateConversationCheckAvailability } from '../services/checkAvailability';
import TextInput from './common/TextInput';
import SearchHighlightText from './common/SearchHighlightText';

// Styled Components

export const CategoryButton = styled.button`
  align-items: center;
  background: ${props =>
    props['aria-checked'] === true ? themeBlue : 'white'};
  border: none;
  border-radius: 15px;
  color: ${props => (props['aria-checked'] === true ? 'white' : themeBlue)};
  cursor: pointer;
  display: ${props => (props.hidden ? 'none' : 'flex')};
  height: fit-content;
  margin: 5px 2px;
  padding: 5px 10px;
  width: fit-content;
  &:focus {
    outline: none;
  }
  &:hover {
    background: ${props =>
      props['aria-checked'] === true ? themeBlue : 'gray'};
    color: white;
    transform: scale(1.01);
  }
`;

export const CategoriesContainer = styled.div`
  align-items: baseline;
  border-top: 1px solid gray;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  padding: 15px 0px 5px 0px;
`;

const ListProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-start;
  overflow: auto;
  padding: 10px;
`;

const NoScrollContainer = styled(ScrollContainer)`
  overflow: hidden;
`;

const SelectableListItem = styled.div`
  border-bottom: 1px solid gray;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  justify-content: space-between;
  padding: 5px 10px;
`;

const SearchContainer = styled.div`
  align-items: center;
  border-top: 1px solid gray;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 15px 20px 5px 20px;
`;

const ShowCategoriesLabel = styled.div`
  align-self: center;
  color: gray;
  cursor: pointer;
  font-size: 12px;
  font-style: italic;
  margin: 5px 0px;
  text-decoration: underline;
  text-align: center;
  &:hover {
    color: white;
  }
`;
// END Styled Components

const CheckAvailabilityProductComponent: React.FC = () => {
  const {
    changingProduct,
    headerErrorMessage,
    setChangingProduct,
    setHeaderErrorMessage,
  } = useCheckAvailability();
  const { conversation, site } = useSession();
  const { pageView } = useParent();

  // guaranteed to exist on mount
  const { checkAvailability } = conversation.value;

  const { allProducts, recommendations } = site.botConfig.checkAvailability;

  const [products, setProducts] = useState(checkAvailability.products);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [filter, setFilter] = useState('');

  async function saveNewProduct(
    products: ConversationCheckAvailabilityProducts
  ) {
    setIsSaving(true);
    setErrorMessage('');

    if (products.products.length === 0) {
      setErrorMessage('Please select at least one tour.');
      setIsSaving(false);
      return;
    } else if (headerErrorMessage) {
      setHeaderErrorMessage('');
    }

    const updatedCheckAvailability: ConversationCheckAvailability = {
      ...checkAvailability,
      products,
    };

    const message = createCheckAvailabilityUserActionMessage(
      `Tour selected: ${getTourDisplayName(products)}`,
      checkAvailability,
      pageView
    );

    const { error } = await updateConversationCheckAvailability({
      conversation,
      message,
      updatedCheckAvailability,
    });
    setIsSaving(false);
    setChangingProduct(false);

    if (error) {
      setErrorMessage('Error saving product selection');
    }
  }

  if (!changingProduct) return null;

  // if searching, filter only matching products. Else all in category
  let filteredProducts: CheckAvailabilityProduct[] = [];

  if (filter || products.category === SEARCH) {
    // actively searching, lets only show products that match
    filteredProducts = allProducts.filter(p =>
      p.name.toLowerCase().includes(filter.toLowerCase())
    );
  } else if (
    products.category !== DEFAULT_CHECK_AVAILABILITY_PRODUCTS.category &&
    Array.isArray(recommendations)
  ) {
    // recommendation categories exist, only show products in that category
    filteredProducts =
      recommendations.find(rec => rec.name === products.category).products ||
      [];
  } else {
    // no recommendation categories exist, show all products
    filteredProducts = allProducts;
  }

  function handleProductSelection(
    product: CheckAvailabilityProduct,
    isSelected: boolean
  ) {
    if (isSelected) {
      if (errorMessage) setErrorMessage('');
      setProducts({
        category: products.category,
        products: products.products.filter(
          p => p.productId !== product.productId
        ),
      });
    } else if (products.products.length >= 5) {
      setErrorMessage('Select maximum (5) tours');
    } else {
      if (errorMessage) setErrorMessage('');
      setProducts({
        category: products.category,
        products: [...products.products, product],
      });
    }
  }

  function handleSearchClick() {
    if (products.category !== SEARCH) {
      setProducts({
        category: SEARCH,
        products: DEFAULT_CHECK_AVAILABILITY_PRODUCTS.products,
      });
    }
  }

  function handleCategorySelection(
    recommendation: CheckAvailabilityRecommendations
  ) {
    setFilter('');
    if (products.category === recommendation.name) {
      // category is currently selected, let's unselect it and show all products
      setProducts({
        category: '',
        products: [],
      });
    } else {
      // select category and filter products
      setProducts({
        category: recommendation.name,
        products: [],
      });
    }
  }

  const recommendationsExist = recommendations && recommendations.length > 0;

  return (
    <Overlay>
      <NoScrollContainer>
        <TitleText>Select up to (5) tours</TitleText>
        {recommendationsExist && products.category !== SEARCH && (
          <CategoriesContainer>
            {recommendations.map(recommendation => (
              <CategoryButton
                aria-checked={products.category === recommendation.name}
                key={recommendation.name}
                onClick={() => handleCategorySelection(recommendation)}
              >
                {recommendation.name}
              </CategoryButton>
            ))}
            <CategoryButton
              onClick={() =>
                setProducts({
                  category: SEARCH,
                  products: [],
                })
              }
              style={{ top: '2px', position: 'relative' }}
            >
              <BsSearch
                size={16}
                color={themeBlue}
                style={{ marginRight: '5px' }}
              />
              Search
            </CategoryButton>
          </CategoriesContainer>
        )}
        {products.category === SEARCH && (
          <SearchContainer>
            <TextInput
              onChange={event => setFilter(event.target.value)}
              onClick={handleSearchClick}
              size="14"
              value={filter}
            />
            <ShowCategoriesLabel
              onClick={() => {
                setFilter('');
                setProducts(DEFAULT_CHECK_AVAILABILITY_PRODUCTS);
              }}
            >
              show categories
            </ShowCategoriesLabel>
          </SearchContainer>
        )}
        <ListProductsContainer>
          {filteredProducts.map(p => {
            const matchingProduct = products.products.find(
              prod => p.name === prod.name
            );
            const isSelected =
              p.productId === (matchingProduct && matchingProduct.productId);

            return (
              <SelectableListItem
                color={isSelected ? 'white' : 'gray'}
                key={p.productId}
                onClick={() => handleProductSelection(p, isSelected)}
              >
                <SearchHighlightText
                  style={{ textDecoration: 'underline' }}
                  text={p.name}
                  search={filter}
                />

                <div style={{ marginLeft: '10px', alignSelf: 'center' }}>
                  {isSelected ? (
                    <FaCheckSquare color={themeBlue} size={16} />
                  ) : (
                    <FaRegSquare color="gray" size={16} />
                  )}
                </div>
              </SelectableListItem>
            );
          })}
        </ListProductsContainer>
      </NoScrollContainer>
      <ButtonsContainer>
        <Button
          color={themeBlue}
          disabled={isSaving}
          onClick={() => saveNewProduct(products)}
        >
          {isSaving ? <SavingSpinner /> : 'SAVE'}
        </Button>
        <Button
          color="gray"
          disabled={isSaving}
          onClick={() => setChangingProduct(false)}
          style={{ marginTop: '10px' }}
        >
          CANCEL
        </Button>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </ButtonsContainer>
    </Overlay>
  );
};

export default CheckAvailabilityProductComponent;
